// QuizCreator.js
import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import c from "./QuizCreator.module.scss";
import siteService from "../../services/SiteService";
import {QuizProvider, useQuiz} from "../../store/QuizContext";
import ImageUploader from "../../components/ui/functional/ImageUploader/ImageUploader";
import OnTopBox from "../../components/ui/onTopBox";
import Checkbox from "../../components/ui/Checkbox";

function QuestionTypeSelect({chooseFunc}) {
	return <div className={c.questionTypeSelect}>
		<button className={c.button} onClick={()=>chooseFunc('multipleChoice')}>Flervalg</button>
		<button className={c.button} onClick={()=>chooseFunc('freeText')}>Fri-tekst</button>
		<button className={c.button} onClick={()=>chooseFunc('trueFalse')}>Ja/nei</button>
	</div>
}

function QuizCreator() {
	const [loaded, setLoaded] = useState(false);
	const {
		quiz,
		setQuiz,
		isImageSelectOpen,
		setQuizPropertyByPath,
		setImageSelectOpen,
		imageTargetPath,
		setImageTargetPath,
		handleImageSelect,
		addStep,
		removeStep,
		handleQuizChange,
		handleStepChange,
		addQuestion,
		removeQuestion,
		handleQuestionChange,
		addAnswer,
		handleAnswerChange,
		saveQuiz,
		deleteQuiz,
		fetchQuiz,
		exportQuiz,
		getImageSrc
	} = useQuiz();

	const {quizId} = useParams();

	// Fetch the quiz data if necessary
	useEffect(() => {
		if (!loaded && quizId && quiz.steps.length === 0) {
			fetchQuiz(quizId);
		}
		setLoaded(true);
	}, [quizId, fetchQuiz]);

	if (!loaded) return <div className={'spinner'}></div>;
	return (
		<div className={c.root}>
			{isImageSelectOpen && <ImageSelectorModal/>}
			<div>
				<button onClick={saveQuiz} className={c.button}>Lagre</button>
				{quiz._id && <>
					<button onClick={deleteQuiz} className={c.button}>Slett</button>
					<a href={`/quiz/${quizId}`} target="_blank">
						<button className={c.button}>Vis</button>
					</a>
					<Link to={`/admin/quizer/${quizId}/innsendinger`}>
						<button className={c.button}>Innsendinger</button>
					</Link>
				</>}
			</div>
			<h2>Lag Quiz</h2>
			<div className={[c.inputGroup, c.settings].join(' ')}>
				<Checkbox label={'Lagre innsendinger'} state={quiz.settings.saveAnswers} setState={(val)=>{setQuizPropertyByPath('settings.saveAnswers', val)}}/>
				{/*<Checkbox label={'Kun for brukere'} state={quiz.settings.usersOnly} setState={(val)=>{setQuizPropertyByPath('settings.usersOnly', val)}}/>
				<Checkbox label={'Tillat "Forrige"'} state={quiz.settings.allowBackwardStep} setState={(val)=>{setQuizPropertyByPath('settings.allowBackwardStep', val)}}/>*/}
				<label>Type: <select name="type" value={quiz.settings.type} onChange={(e) => setQuizPropertyByPath('settings.type', e.target.value)} className={c.select}>
					<option value={'quiz'}>Quiz</option>
					<option value={'opinionPoll'}>Meningsmåling</option>
					<option value={'form'}>Skjema</option>
				</select>
				</label>
				<p><i>For typisk avstemming, sett type til "Meningsmåling", og opprett et "flervalg" spørsmål.</i></p>
			</div>

			<div className={c.inputGroup}>
				<label className={c.label}>Tittel:</label>
				<input type="text" name="name" value={quiz.name} onChange={handleQuizChange} required
					   className={c.input}/>
			</div>

			<ImageSection imageName={quiz.image} targetPath={'image'}/>

			<div className={c.inputGroup}>
				<label className={c.label}>Innledning:</label>
				<textarea name="description" value={quiz.description} onChange={handleQuizChange}
						  placeholder={'valgfritt'} className={c.textarea}></textarea>
			</div>
			<hr style={{width: '100%'}}/>
			<h3>Steg</h3>
			{quiz.steps.map((step, stepIndex) => (
				<StepEditor key={stepIndex} step={step} stepIndex={stepIndex}/>
			))}
			<div>
				<button onClick={addStep} className={c.button}>Nytt steg</button>
			</div>

			<h3>Quiz JSON Preview</h3>
			<details><pre className={c.preview}>{JSON.stringify(quiz, null, 2)}</pre></details>
		</div>
	);
}

function StepEditor({step, stepIndex}) {
	const {
		removeStep,
		handleStepChange,
		addQuestion,
		handleQuestionChange,
		removeQuestion,
		handleAnswerChange,
		addAnswer,
		getImageSrc,
		setImageTargetPath,
		setImageSelectOpen,
	} = useQuiz();

	const [isQuestionTypeSelectOpen, setIsQuestionTypeSelectOpen] = useState(false);

	return (
		<details key={stepIndex} className={c.step}>
			<summary>
				<h4>{stepIndex + 1} {step.name&&' - '+step.name}</h4>
				<button onClick={() => removeStep(stepIndex)} className={`${c.button} ${c.removeButton}`}>Fjern
				</button>
			</summary>

			<div className={c.inputGroup}>
				<label className={c.label}>Steg Tittel:</label>
				<input type="text" name="name" placeholder={'valgfritt'} value={step.name} onChange={(e) => handleStepChange(stepIndex, e)}
					   className={c.input}/>
			</div>

			<ImageSection imageName={step.image} targetPath={'steps.' + stepIndex + '.image'}/>

			<h5>Spørsmål</h5>
			{step.questions.map((question, questionIndex) => (
				<Question question={question} key={questionIndex} questionIndex={questionIndex} step={step}
						  stepIndex={stepIndex}
						  handleQuestionChange={handleQuestionChange}
						  addAnswer={addAnswer}
						  handleAnswerChange={handleAnswerChange}
						  removeQuestion={removeQuestion}
				/>
			))}
			<button onClick={() => setIsQuestionTypeSelectOpen(true)} className={c.button}>Nytt spørsmål</button>
			<OnTopBox onTopEnabled={isQuestionTypeSelectOpen}
					  setOnTopEnabled={() => setIsQuestionTypeSelectOpen(false)}>
				<QuestionTypeSelect chooseFunc={(type) => {
					addQuestion(stepIndex, type);
					setIsQuestionTypeSelectOpen(false);
				}}/>
			</OnTopBox>
		</details>
	);
}

function Question({question, questionIndex, stepIndex}) {
	const {removeQuestion, handleQuestionChange, addAnswer, handleAnswerChange} = useQuiz();

	return (
		<div key={questionIndex} className={c.question}>
			<div className={c.inputGroup}>
				<div>
					<label>Spørsmål {questionIndex + 1} - {question.type}</label>
					<button onClick={() => removeQuestion(stepIndex, questionIndex)}
							className={`${c.button} ${c.removeButton}`}>Fjern
					</button>
				</div>
				<label className={c.label}>Spørsmål tekst:</label>
				<input type="text" name="text" value={question.text}
					   onChange={(e) => handleQuestionChange(stepIndex, questionIndex, e)}
					   className={c.input}/>
				<ImageSection imageName={question.image} targetPath={'steps.' + stepIndex + '.questions.' + questionIndex + '.image'}/>
			</div>

			{question.type === 'multipleChoice' && (
				<>
					<h6>Svar</h6>
					{question.answers.map((answer, answerIndex) => (
						<div key={answerIndex} className={c.answer}>
							<label className={c.label}>Svar tekst:</label>
							<input type="text" name="text" value={answer.text}
								   onChange={(e) => handleAnswerChange(stepIndex, questionIndex, answerIndex, e)}
								   required className={c.input}/>
							<label className={c.checkboxLabel}>
								<input type="checkbox" name="correct" checked={answer.correct}
									   onChange={(e) => handleAnswerChange(stepIndex, questionIndex, answerIndex, e)}
									   className={c.checkbox}/>
								Riktig
							</label>
						</div>
					))}
					{question.type!=="trueFalse"&&<button onClick={() => addAnswer(stepIndex, questionIndex)} className={c.button}>Nytt svar</button>}
				</>
			)}
			{question.type === 'trueFalse' && (
				<p>Ja/nei telles ikke som korrekt eller feil. Kun for ting vi vil vite. eks: "Vil du få eksklusivt tilbud via email?"</p>
			)}
		</div>
	);
}

function ImageSelectorModal() {
	const {setImageSelectOpen, handleImageSelect, getImageSrc} = useQuiz();
	const [images, setImages] = React.useState([]);
	const {quiz} = useQuiz();

	const fetchImages = async () => {
		const response = await fetch(`${siteService.apiPath}/quiz/${quiz._id}/images`);
		const data = await response.json();
		setImages(data);
	};

	useEffect(() => {
		fetchImages();
	}, []);

	return (
		<div className={c.modalOverlay}>
			<div className={c.modalContent}>
				<ImageUploader extraFormData={{doCompress: true}}
							   path={'quiz' + '/' + quiz._id} cb={fetchImages}/>
				<h3>Select an Image</h3>
				<div className={c.imageGrid}>
					<button className={c.imageItem} onClick={() => handleImageSelect('')}>
						Ingen
					</button>
					{images.map((image) => (
						<div key={image} className={c.imageItem} onClick={() => handleImageSelect(image)}>
							<img src={getImageSrc(image)} alt={image}/>
						</div>
					))}
				</div>
				<button onClick={() => setImageSelectOpen(false)}>Lukk</button>
			</div>
		</div>
	);
}

function ImageSection({targetPath, imageName}) {
	const {setImageTargetPath, setImageSelectOpen, getImageSrc} = useQuiz();

	return (<div className={c.inputGroup}>
			{imageName && <img src={getImageSrc(imageName)}/>}
			<button className={c.openImageModal} onClick={()=>{
				setImageTargetPath(targetPath);
				setImageSelectOpen(true);
			}}>Velg bilde
			</button>
		</div>
	)
}

// Wrap QuizCreator in QuizProvider to provide context
export default function QuizCreatorWrapper({quizIn, isNew}) {
	return (
		<QuizProvider quizIn={quizIn} isNew={isNew}>
			<QuizCreator/>
		</QuizProvider>
	);
}
